import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ProgressBar } from 'primeng/progressbar';
import { Message } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import {
  CadFile,
  cadUploadFileTypeError,
  isCadUploadFile,
} from '../../../../../../../models/cad-file.model';
import { QuoteService } from '../../../../../../quote.service';
import { UploadCadComponent } from '../../../../../../shared/upload-cad/upload-cad.component';
import { UseExistingCadComponent } from '../../../../../../shared/use-existing-cad/use-existing-cad.component';

@Component({
  selector: 'app-quote-item-upload-file',
  imports: [
    ProgressBar,
    Message,
    TabsModule,
    SharedConfirmDialogComponent,
    UploadCadComponent,
    UseExistingCadComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './quote-item-upload-file.component.html',
  styleUrl: './quote-item-upload-file.component.scss',
})
export class QuoteItemUploadFileComponent {
  private quoteService = inject(QuoteService);
  private confirmationService = inject(ConfirmationService);

  @Input() isBusy = false;
  @Input() progress = 0;
  @Input() statusText = '';
  @Input() processingError: string | null = null;
  @Input() cadFileNames: string[] = [];
  @Input() multiple = false;

  @Output() filesUploadRequested = new EventEmitter<File[]>();
  @Output() filesUploaded = new EventEmitter<CadFile[]>();
  @Output() stateCleared = new EventEmitter<void>();

  activeTab: 'upload' | 'existing' = 'upload';

  fileTypeError = '';

  setActiveTab(tab: 'upload' | 'existing'): void {
    this.activeTab = tab;
    this.fileTypeError = '';
  }

  onExistingCadFilesSelected(files: CadFile[]): void {
    const quoteCompany = this.quoteService.quote.company;
    const hasCompanyMismatch = files.some(
      (f) => f.company !== null && f.company !== quoteCompany,
    );

    if (hasCompanyMismatch) {
      this.confirmationService.confirm({
        header: 'Different Customer',
        message:
          'The selected CAD file is associated with a different customer than this quote. Are you sure you want to link it?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Link anyway',
        rejectLabel: 'Cancel',
        accept: () => {
          this.resetState();
          this.filesUploaded.emit(files);
        },
      });
      return;
    }

    this.resetState();
    this.filesUploaded.emit(files);
  }

  private isValidFileType(file: File): boolean {
    return isCadUploadFile(file);
  }

  onFilesSelected(files: File[]): void {
    if (!files || files.length === 0) return;

    const invalidFiles = files.filter((file) => !this.isValidFileType(file));
    if (invalidFiles.length > 0) {
      this.fileTypeError = cadUploadFileTypeError(
        invalidFiles.map((f) => f.name),
      );
      return;
    }

    this.fileTypeError = '';
    this.resetState();
    this.filesUploadRequested.emit(files);
  }

  resetState(): void {
    this.stateCleared.emit();
  }
}
